import React, { useState } from 'react';
import { Link } from 'react-router-dom';

const sb = { borderLeft:'3px solid var(--gold)', padding:'.75rem 1.5rem', background:'rgba(197,165,90,0.04)' };
const sbText = { fontFamily:"'Playfair Display',serif", fontStyle:'italic', fontSize:'1.05rem', lineHeight:1.8, color:'rgba(245,240,232,0.92)', marginBottom:'.25rem' };
const sbRef = { fontFamily:"'Oswald',sans-serif", fontSize:'.8rem', letterSpacing:2, color:'var(--gold)' };

const FAQS = [
  {
    category: 'About the KJV',
    questions: [
      {
        q: 'Why do you only use the King James Version?',
        a: [
          'We believe the King James Bible is the preserved word of God for the English-speaking people.',
          'Modern versions systematically weaken the deity of Christ, remove entire verses, and soften the language of judgment. We document these changes in detail on our "Why the KJV" page.',
          'We do not take this position lightly. We take it because we believe God promised to preserve His words (Psalm 12:6-7), and we believe He kept that promise in the King James Bible.',
        ],
        verse: '"The words of the LORD are pure words: as silver tried in a furnace of earth, purified seven times. Thou shalt keep them, O LORD, thou shalt preserve them from this generation for ever."',
        ref: 'Psalm 12:6-7',
        link: { to:'/why-kjv', text:'Read the full comparison' },
      },
      {
        q: 'Are you saying Christians can\'t be saved if they don\'t use a KJV?',
        a: [
          'Absolutely not. Salvation is by grace through faith in Jesus Christ alone.',
          'Our position is not that the KJV is required for salvation. Our position is that we believe a Christian who wants to grow, study, and understand the full counsel of God will be best served by the King James Bible because it is perfect and inerrant.',
        ],
        verse: '"For whosoever shall call upon the name of the Lord shall be saved."',
        ref: 'Romans 10:13',
      },
      {
        q: 'Isn\'t the KJV too hard to understand?',
        a: [
          'The King James Bible reads at approximately a 5th-grade reading level. Most of the words that people find "difficult" are not actually difficult at all; they are simply unfamiliar because they are no longer used in everyday speech. Words like "thee," "thou," and "ye" are actually more precise than their modern equivalents, because they distinguish between singular and plural "you."',
          'The real question is not whether you can understand the KJV. The question is whether you are willing to invest the effort. The Bible was never meant to be casual reading. It is the word of God. It deserves careful, patient study.',
          'We also provide a complete Bible reader and search tool right here on this site to help you study the KJV for yourself.',
        ],
        verse: '"Study to shew thyself approved unto God, a workman that needeth not to be ashamed, rightly dividing the word of truth."',
        ref: '2 Timothy 2:15',
        link: { to:'/bible', text:'Read the KJV Bible' },
      },
      {
        q: 'What about the New King James Version (NKJV)?',
        a: [
          'The NKJV is often presented as a simple modernization of the KJV, keeping the same text in updated language. This is not entirely accurate. While the NKJV does use the Textus Receptus as its primary base, it also includes extensive footnotes that reference the Alexandrian manuscripts, directing the reader toward the same corrupted texts that underlie the NIV and ESV.',
          'The NKJV also makes changes to key doctrinal words. For example, it changes "repent" to "relent" when referring to God (Exodus 32:14), changes "new testament" to "new covenant" (removing the connection to the document structure of the Bible), and alters numerous other passages that affect meaning.',
        ],
        verse: '"Every word of God is pure: he is a shield unto them that put their trust in him. Add thou not unto his words, lest he reprove thee, and thou be found a liar."',
        ref: 'Proverbs 30:5-6',
      },
    ],
  },
  {
    category: 'About This Site',
    questions: [
      {
        q: 'What is the purpose of this website?',
        a: [
          'KJVUSA.org exists for three purposes. First, to present the gospel of Jesus Christ clearly and without compromise, so that anyone who visits this site can understand how to be saved. Second, to defend the King James Bible as the preserved word of God and expose the changes made by modern translations. Third, to document America\'s Christian heritage and call this nation back to the God who blessed it.',
          'Everything on this site points back to these three purposes. Whether you are reading about state mottos, Bible version comparisons, or the most patriotic thing an American can do, the message is the same: Jesus Christ is the answer.',
        ],
        verse: '"For I am not ashamed of the gospel of Christ: for it is the power of God unto salvation to every one that believeth."',
        ref: 'Romans 1:16',
      },
      {
        q: 'Are you saying other religions are wrong?',
        a: [
          'We are saying what Jesus Christ said: "I am the way, the truth, and the life: no man cometh unto the Father, but by me" (John 14:6). We did not write those words. Jesus did. We simply believe them.',
          'We treat people of all faiths with respect and dignity, while trying to help them instead place their faith in Jesus Christ. We do not mock, harass, or belittle anyone. We do, however, believe that truth matters and that eternity is at stake. If the Bible is true (which it is), then there is only ONE way to be saved, and love demands that we share it plainly.',
        ],
        verse: '"Neither is there salvation in any other: for there is none other name under heaven given among men, whereby we must be saved."',
        ref: 'Acts 4:12',
      },
    ],
  },
  {
    category: 'About Salvation',
    questions: [
      {
        q: 'Can I lose my salvation?',
        a: [
          'The Bible teaches that salvation is a gift from God that cannot be lost. When you trust in Jesus Christ, you are sealed by the Holy Spirit and given eternal life. "Eternal" means eternal; it does not mean "temporary until you mess up."',
          'This does not mean a saved person can live however they want without consequence. God chastens His children (Hebrews 12:6). Salvation is not a license to sin; it is a deliverance from the power and penalty of sin.',
          'The security of the believer rests not on the believer\'s performance, but on the finished work of Jesus Christ. If your salvation depended on you keeping it, it would not be a gift. It would be a wage.',
        ],
        verse: '"And I give unto them eternal life; and they shall never perish, neither shall any man pluck them out of my hand."',
        ref: 'John 10:28',
      },
      {
        q: 'Do I need to be baptized to be saved?',
        a: [
          'Baptism is an important act of obedience for a believer, yet it is not a requirement for salvation. The Bible is clear that salvation is by grace through faith alone (Ephesians 2:8-9). The thief on the cross was saved without ever being baptized (Luke 23:43).',
          'Baptism is a public declaration of an inward decision that has already been made. It is an outward picture of the death, burial, and resurrection of Jesus Christ. It does not save you; it identifies you with the One who did.',
          'We encourage every new believer to be baptized as soon as possible as an act of obedience to Christ.',
        ],
        verse: '"For Christ sent me not to baptize, but to preach the gospel."',
        ref: '1 Corinthians 1:17',
      },
      {
        q: 'What if I\'ve done things that are too bad to be forgiven?',
        a: [
          'There is no sin too great for the blood of Jesus Christ. The Apostle Paul, before he was saved, murdered Christians. He called himself the "chief of sinners" (1 Timothy 1:15). God saved him and used him to write nearly half the New Testament.',
          'The Bible says "where sin abounded, grace did much more abound" (Romans 5:20). There is no category of sin that falls outside the reach of Christ\'s sacrifice. He died for all sin, for all people, for all time.',
          'The only sin that will send you to hell is the sin of rejecting Jesus Christ. If you will believe on Him today, every sin you have ever committed is paid for in full.',
        ],
        verse: '"Come now, and let us reason together, saith the LORD: though your sins be as scarlet, they shall be as white as snow; though they be red like crimson, they shall be as wool."',
        ref: 'Isaiah 1:18',
        link: { to:'/salvation', text:'Learn how to be saved' },
      },
    ],
  },
];

function FAQItem({ faq }) {
  const [open, setOpen] = useState(false);
  return (
    <div style={{ border:'1px solid rgba(197,165,90,0.1)', marginBottom:'.5rem' }}>
      <button onClick={() => setOpen(!open)} style={{
        width:'100%', display:'flex', justifyContent:'space-between', alignItems:'center',
        padding:'1.25rem 1.5rem', background: open ? 'rgba(197,165,90,0.06)' : 'rgba(255,255,255,0.02)',
        border:'none', cursor:'pointer', textAlign:'left', transition:'.3s',
      }}>
        <span style={{ fontFamily:"'Playfair Display',serif", fontSize:'1.05rem', color:'var(--white)', fontWeight:600, paddingRight:'1rem', lineHeight:1.4 }}>{faq.q}</span>
        <span style={{ color:'var(--gold)', transition:'.3s', transform: open ? 'rotate(180deg)' : '', flexShrink:0, fontSize:'.8rem' }}>&#9662;</span>
      </button>
      {open && (
        <div style={{ padding:'1.25rem 1.5rem 1.5rem', background:'rgba(0,0,0,0.08)' }}>
          {faq.a.map((p,i) => (
            <p key={i} style={{ fontSize:'.92rem', lineHeight:1.9, color:'rgba(245,240,232,0.8)', marginBottom: i < faq.a.length-1 ? '.75rem' : '1rem' }}>{p}</p>
          ))}
          {faq.verse && (
            <div style={{ ...sb, margin:'1rem 0' }}>
              <p style={{ ...sbText, fontSize:'.95rem' }}>{faq.verse}</p>
              <p style={sbRef}>{faq.ref}</p>
            </div>
          )}
          {faq.link && (
            <Link to={faq.link.to} style={{ fontFamily:"'Oswald',sans-serif", fontSize:'.8rem', letterSpacing:2, textTransform:'uppercase', color:'var(--gold)', textDecoration:'none' }}>
              {faq.link.text} →
            </Link>
          )}
        </div>
      )}
    </div>
  );
}

export default function FAQ() {
  return (
    <>
      {/* HERO */}
      <section className="hero" style={{ background:'linear-gradient(165deg,var(--navy) 0%,var(--deep-blue) 40%,var(--royal) 100%)', minHeight:'auto', padding:'5rem 2rem 4rem' }}>
        <div className="inner" style={{ textAlign:'center' }}>
          <div className="section-label center">Common Questions, Biblical Answers</div>
          <h1 className="section-title" style={{ fontSize:'clamp(1.8rem,5vw,3.2rem)', marginBottom:'1.25rem', lineHeight:1.15 }}>
            Frequently Asked<br />Questions
          </h1>
          <p style={{ fontSize:'1.1rem', fontFamily:"'Playfair Display',serif", fontStyle:'italic', color:'rgba(245,240,232,0.8)', maxWidth:600, margin:'0 auto 2rem' }}>
            Honest questions deserve honest answers. Here are the ones we hear most often.
          </p>
        </div>
      </section>

      <div className="divider" />

      {/* FAQ SECTIONS */}
      {FAQS.map((section,si) => (
        <React.Fragment key={si}>
          <section style={{ background: si % 2 === 0 ? 'linear-gradient(180deg,var(--navy),var(--deep-blue))' : 'var(--deep-blue)' }}>
            <div className="inner">
              <h2 className="section-title" style={{ fontSize:'clamp(1.3rem,3vw,1.8rem)' }}>{section.category}</h2>
              {section.questions.map((faq,qi) => <FAQItem key={qi} faq={faq} />)}
            </div>
          </section>
          {si < FAQS.length - 1 && <div className="divider divider-gold" />}
        </React.Fragment>
      ))}

      <div className="divider" />

      {/* CTA */}
      <section style={{ background:'linear-gradient(165deg,#1B3A6B 0%,var(--deep-blue) 50%,var(--navy) 100%)', textAlign:'center', padding:'5rem 2rem' }}>
        <div className="inner" style={{ maxWidth:700 }}>
          <h2 className="section-title" style={{ fontSize:'clamp(1.4rem,3vw,2rem)', marginBottom:'1.5rem' }}>
            Still Have Questions?
          </h2>
          <p style={{ fontSize:'1.05rem', lineHeight:1.9, color:'rgba(245,240,232,0.8)', maxWidth:550, margin:'0 auto 2rem' }}>
            The best place to find answers is in the Bible itself. We encourage you to read the Gospel of John, the book God wrote specifically so that you might believe and have life through His name.
          </p>
          <div style={{ display:'flex', gap:'1.5rem', justifyContent:'center', flexWrap:'wrap' }}>
            <Link to="/salvation" className="btn-p">How to Be Saved</Link>
            <Link to="/bible/43/1" className="btn-s">Read the Gospel of John</Link>
          </div>
        </div>
      </section>
    </>
  );
}
